import React, { ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Translate, useTranslate } from '~/i18n';
import Dropdown from '~/components/common/dropdown';
import { Input } from '~/components/common/forms';
import useDebounce from '~/utils/useDebounce';
import { CloseIcon, SearchIcon } from '~/svgs';
import { SubmitLoader } from './loaders/submit';

type FilterProps = {
   onChange?: (tag: Record<string, any>, isChecked: boolean) => void;
   onSearch?: (searchTerm: string, isCustomTag?: boolean) => void;
   isLoading: boolean;
   defaultOptions?: Record<string, any>;
   optionList?: any;
   placeholder?: ReactNode;
};
const SearchFilter = ({
   onChange,
   onSearch,
   isLoading,
   defaultOptions = [],
   optionList = [],
   placeholder,
}: FilterProps) => {
   const { lang, translate } = useTranslate();
   const [open, setOpen] = useState(false);
   const [searchValue, setSearchValue] = useState('');
   const dropdownToggler = () => setOpen(true);
   const dropdownClose = () => setOpen(false);
   const debouncedValue = useDebounce(searchValue, 1000);
   useEffect(() => {
      onSearch?.(debouncedValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [debouncedValue]);

   // const selectedListWithNames = useMemo(() => {
   //    return optionList?.filter((item: any) => defaultOptions?.includes(item._id));
   //    // eslint-disable-next-line react-hooks/exhaustive-deps
   // }, [defaultOptions]);

   return (
      <div className="flex flex-col w-full">
         <Dropdown
            listClassName="p-4 !border-none w-full shadow-md z-10 !overflow-y-auto max-h-40"
            toggler={
               <div className="h-full pb-2">
                  <Input
                     onChange={(e: any) => setSearchValue(e.target.value)}
                     onFocus={dropdownToggler}
                     onKeyDown={(e: any) => {
                        if (e.key === 'Enter') {
                           setSearchValue('');
                           onSearch?.(e.target.value, true);
                           e.target.value = '';
                        }
                     }}
                     className="ltr:!border-r-0 rtl:!border-l-0"
                     inputWrapperClassName="[&>*]:hover:border-primary"
                     placeholder={
                        placeholder ? placeholder : translate({ id: 'bookings:filter by' })
                     }
                     appendClassName="!bg-white"
                     append={<SearchIcon className="w-8" />}
                  />
               </div>
            }
            open={open && debouncedValue?.length}
            toggleMenu={dropdownClose}>
            {isLoading ? (
               <div className="flex items-center justify-center h-20">
                  <SubmitLoader />
               </div>
            ) : optionList?.length ? (
               optionList.map((option: any, index: number) => {
                  return (
                     <div
                        key={index}
                        className="flex items-center py-1 pb-2 space-x-2 border-b rtl:space-x-reverse">
                        <input
                           id={option._id}
                           defaultChecked={
                              !!defaultOptions?.find(
                                 (tag: { _id: string }) => tag?._id === option._id
                              )
                           }
                           onChange={e => {
                              if (e.target.checked) {
                                 !defaultOptions?.find(
                                    (tag: { _id: string }) => tag?._id === option._id
                                 ) && onChange?.({ _id: option._id, ...option.name }, true);
                              } else {
                                 onChange?.({ _id: option._id, ...option.name }, false);
                              }
                           }}
                           type="checkbox"
                           className="border-gray-300 rounded-sm focus:ring-white peer"
                        />
                        <div className="text-sm capitalize text-grey-200 peer-checked:text-primary peer-checked:font-semibold">
                           {option.name?.[lang] || option.name?.en}
                        </div>
                     </div>
                  );
               })
            ) : (
               <div className="flex items-center justify-center">
                  <span className="m-10 mx-4 text-xl">
                     <Translate id={'common:no results'} />
                  </span>
               </div>
            )}
         </Dropdown>
         {defaultOptions?.length ? (
            <motion.div
               layout
               layoutId={'deafult-list'}
               className="flex flex-wrap w-full gap-2 my-2">
               <AnimatePresence>
                  {defaultOptions?.map((tag: Record<string, any>) => {
                     return (
                        <motion.div
                           initial={{ opacity: 0, scale: 0.5 }}
                           animate={{ opacity: 1, scale: 1 }}
                           exit={{ opacity: 0 }}
                           key={tag?._id}>
                           <div
                              key={tag?._id}
                              className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-white rounded-full bg-primary w-fit">
                              <span>{tag?.[lang] || tag?.[`tag_${lang}`]}</span>
                              <button
                                 onClick={() => {
                                    onChange?.(tag, false);
                                 }}
                                 type="button"
                                 className="rounded-full hover:bg-white focus:outline-none focus:ring-0 group">
                                 <CloseIcon
                                    className="[&>*]:fill-white group-hover:[&>*]:fill-black"
                                    width={16}
                                    height={16}
                                 />
                              </button>
                           </div>
                        </motion.div>
                     );
                  })}
               </AnimatePresence>
            </motion.div>
         ) : null}
      </div>
   );
};

export default SearchFilter;
